import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Divider,
  Rating,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FiMaximize2, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import GanttTimeline from "components/GanttTimeline";
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useTranslation } from "react-i18next";

const getAge = (birthDate) => {
  if (!birthDate) return '';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const StatusBadge = ({ active }) => {
  const { t } = useTranslation();
  return (
  <VuiBox
    component="span"
    sx={theme => ({
      display: 'inline-block',
      px: 2,
      py: 0.5,
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: 14,
      background: active ? theme.palette.success.main : theme.palette.error.main,
      color: theme.palette.white.main,
      ml: 2,
      letterSpacing: 0.5,
      boxShadow: active ? '0 2px 8px 0 rgba(67,160,71,0.15)' : '0 2px 8px 0 rgba(229,57,53,0.15)',
    })}
  >
    {active ? t('driver_table.active') : t('driver_table.inactive')}
  </VuiBox>
)};

const Section = ({ title, children }) => {
  const { t } = useTranslation();
  return (
  <VuiBox mb={3}>
    <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
      {t(title)}
    </VuiTypography>
    <VuiBox
      sx={{
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        p: 2,
      }}
    >
      {children}
    </VuiBox>
  </VuiBox>
)};

const MAP_API_KEY = '5b3ce3597851110001cf6248550fa534be8c488e979060ce46c4a878';

// Fix default marker icon issue in leaflet
if (typeof window !== 'undefined' && L && L.Icon && L.Icon.Default) {
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
  });
}

// Custom SVG pin icon (red Google Maps style)
const pinSvg = encodeURIComponent(`
<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d_1_2)">
    <ellipse cx="16" cy="13" rx="7" ry="7" fill="#EA4335"/>
    <ellipse cx="16" cy="13" rx="3" ry="3" fill="white"/>
    <path d="M16 30C16 30 26 20.5 23 13C20.5 7.5 16 7.5 16 7.5C16 7.5 11.5 7.5 9 13C6 20.5 16 30 16 30Z" fill="#EA4335"/>
  </g>
  <defs>
    <filter id="filter0_d_1_2" x="0" y="0" width="32" height="32" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="2"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_2"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_2" result="shape"/>
    </filter>
  </defs>
</svg>
`);
const pinIcon = new L.Icon({
  iconUrl: `data:image/svg+xml,${pinSvg}`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
});

const DriverLocationMap = ({ lat, lng, name, height = 200, fullscreen = false, onExpand }) => {
  const { t } = useTranslation();
  if (!lat || !lng) return null;
  const position = [parseFloat(lat), parseFloat(lng)];
  return (
    <div style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden', zIndex: 1, position: 'relative' }}>
      {onExpand && !fullscreen && (
        <button
          onClick={onExpand}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1000,
            background: 'rgba(34,34,58,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          title={t('common.expand_map')}
        >
          <FiMaximize2 size={20} />
        </button>
      )}
      <MapContainer
        center={position}
        zoom={15}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={true}
        dragging={true}
        doubleClickZoom={true}
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
        />
        <Marker position={position} icon={pinIcon}>
          <Popup>
            {name}<br />{t('driver_table.current_location')}
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

const DriverDetailsModal = ({ open, onClose, driver, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, group: '', images: [], index: 0 });
  const [selectedJourneyIdx, setSelectedJourneyIdx] = useState(0);
  const [journeyMapFullscreen, setJourneyMapFullscreen] = useState(false);
  const [showPaths, setShowPaths] = useState({ supposed: true, arriving: true, taken: true });

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }));
      } else if (e.key === 'ArrowRight') {
        setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }));
      } else if (e.key === 'Escape') {
        setLightbox(l => ({ ...l, open: false }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.open, lightbox.images.length]);

  if (!driver) return null;

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#191932',
          color: '#fff',
          borderRadius: '18px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        },
      }}
    >
      {/* Header */}
      <VuiBox
        display="flex"
        alignItems="center"
        gap={3}
        px={4}
        pt={4}
        pb={2}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'linear-gradient(90deg, #22223a 60%, #1a1a2e 100%)',
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          position: 'relative',
        }}
      >
        {/* Action icons at top right */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              color: '#ffa726',
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              '&:hover': {
                background: '#ffa726',
                color: '#fff',
              },
            }}
            onClick={() => {
              onClose();
              if (onEdit) onEdit(driver);
            }}
            title={t('common.edit')}
          >
            <FaEdit size={20} />
          </Box>
          <Box
            sx={{
              cursor: 'pointer',
              color: '#e53935',
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              '&:hover': {
                background: '#e53935',
                color: '#fff',
              },
            }}
            onClick={() => {
              onClose();
              if (onDelete) onDelete(driver);
            }}
            title={t('driver_table.delete')}
          >
            <FaTrash size={20} />
          </Box>
          <Box
            sx={{
              cursor: 'pointer',
              color: '#fff',
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              '&:hover': {
                background: '#2196f3',
                color: '#fff',
              },
            }}
            onClick={onClose}
            title={t('common.close')}
          >
            <FiX size={24} />
          </Box>
        </Box>
        <Avatar
          src={driver.profile_photos?.[0] || driver.profile_photo}
          alt={`${driver.first_name} ${driver.last_name}`}
          sx={{ width: 72, height: 72, border: '3px solid #353570' }}
        />
        <VuiBox>
          <VuiTypography variant="h4" color="white" fontWeight="bold" mb={0.5}>
            {driver.first_name} {driver.last_name}
            <StatusBadge active={driver.active} />
          </VuiTypography>
          <VuiTypography variant="caption" color="text" fontWeight="regular">
            {driver.phone}
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <DialogContent sx={{ px: 4, pt: 3, pb: 0 }}>
        {/* Basic Info */}
        <Section title="driver_table.basic_information">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('driver_table.gender')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {t(`driver_table.${driver.gender}`)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('driver_table.age')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {getAge(driver.birth_date)}
              </VuiTypography>
            </Grid>
          </Grid>
        </Section>

        {/* Profile Photos */}
        {driver.profile_photos && driver.profile_photos.length > 0 && (
          <Section title="driver_table.profile_photos">
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 1 }}>
              {driver.profile_photos.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`Profile ${idx + 1}`}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, border: '2px solid #353570', background: '#232345', cursor: 'pointer' }}
                  onClick={() => setLightbox({ open: true, group: 'Profile Photos', images: driver.profile_photos, index: idx })}
                />
              ))}
            </Box>
          </Section>
        )}
        {/* Identification Images */}
        {driver.identification_images && driver.identification_images.length > 0 && (
          <Section title="driver_table.identification_images">
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 1 }}>
              {driver.identification_images.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`Identification ${idx + 1}`}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, border: '2px solid #353570', background: '#232345', cursor: 'pointer' }}
                  onClick={() => setLightbox({ open: true, group: 'Identification Images', images: driver.identification_images, index: idx })}
                />
              ))}
            </Box>
          </Section>
        )}
        {/* Driver License Images */}
        {driver.driver_license && driver.driver_license.length > 0 && (
          <Section title="driver_table.driver_license">
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 1 }}>
              {driver.driver_license.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`Driver License ${idx + 1}`}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, border: '2px solid #353570', background: '#232345', cursor: 'pointer' }}
                  onClick={() => setLightbox({ open: true, group: 'Driver License Images', images: driver.driver_license, index: idx })}
                />
              ))}
            </Box>
          </Section>
        )}

        {/* Location */}
        <Section title="driver_table.current_location">
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={12}>
              <DriverLocationMap
                lat={driver.current_latitude}
                lng={driver.current_longitude}
                name={`${driver.first_name} ${driver.last_name}`}
                onExpand={() => setMapFullscreen(true)}
              />
            </Grid>
          </Grid>
        </Section>

        {/* Ratings & Reviews */}
        <Section title="driver_table.ratings_reviews">
          <VuiBox mb={2} display="flex" alignItems="center" gap={2}>
            <Rating value={driver.rating} readOnly precision={0.5} size="medium" />
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {driver.rating.toFixed(1)}
            </VuiTypography>
            <VuiTypography variant="caption" color="text">
              ({driver.rating_count} {t('driver_table.reviews')})
            </VuiTypography>
          </VuiBox>
          {driver.ratings?.map((rating, index) => (
            <VuiBox key={index} mb={2} p={2} display="flex" alignItems="flex-start" gap={2} sx={{ background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
              <Avatar
                src={rating.client.profile_photo}
                alt={`${rating.client.first_name} ${rating.client.last_name}`}
                sx={{ width: 40, height: 40, border: '2px solid #353570' }}
              />
              <VuiBox flex={1}>
                <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <VuiTypography variant="button" color="white" fontWeight="bold">
                    {rating.client.first_name} {rating.client.last_name}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    {formatDate(rating.created_at)}
                  </VuiTypography>
                </VuiBox>
                <VuiBox display="flex" alignItems="center" gap={1} mt={0.5} mb={0.5}>
                  <Rating value={rating.rating_value} readOnly size="small" />
                </VuiBox>
                <VuiTypography variant="caption" color="white">
                  {rating.review}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          ))}
        </Section>

        {/* Schedules */}
        <Section title="driver_table.schedules">
          <GanttTimeline schedules={driver.schedules} driver={driver} />
        </Section>

        {/* Journey Log */}
        {driver.journeys && driver.journeys.length > 0 && (
          <Section title="driver_table.journey_log">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {driver.journeys.map((journey, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 3,
                    borderRadius: 3,
                    background: idx === selectedJourneyIdx ? 'rgba(33,150,243,0.15)' : 'rgba(255,255,255,0.04)',
                    border: idx === selectedJourneyIdx ? '2px solid #2196f3' : '2px solid transparent',
                    boxShadow: idx === selectedJourneyIdx ? '0 2px 12px 0 rgba(33,150,243,0.10)' : 'none',
                    cursor: 'pointer',
                    mb: 2,
                  }}
                  onClick={() => setSelectedJourneyIdx(idx)}
                >
                  {/* Client Info (top) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar src={journey.client?.profile_photo} alt={journey.client?.first_name} sx={{ width: 44, height: 44, border: '2px solid #353570', mr: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {journey.client?.first_name} {journey.client?.last_name}
                      </VuiTypography>
                      <VuiTypography variant="caption" color="text" sx={{ mt: 0.5 }}>
                        {journey.client?.phone}
                      </VuiTypography>
                    </Box>
                  </Box>
                  {/* Journey Info (below client info) */}
                  <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {journey.source_address} → {journey.destination_address}
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" sx={{ display: 'block', mt: 1 }}>
                      {t('driver_table.duration')}: {journey.estimated_duration} | {t('driver_table.distance')}: {journey.estimated_distance} | {t('driver_table.cost')}: {journey.estimated_cost}
                    </VuiTypography>
                  </Box>
                </Box>
              ))}
            </Box>
            {/* Map for selected journey */}
            {driver.journeys[selectedJourneyIdx] && (
              <>
                {/* Path toggles */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3, mb: 1, ml: '13px' }}>
                  <FormControlLabel
                    control={<Checkbox checked={showPaths.supposed} onChange={(e) => setShowPaths({...showPaths, supposed: e.target.checked})} />}
                    label={t('driver_table.supposed_path')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={showPaths.arriving} onChange={(e) => setShowPaths({...showPaths, arriving: e.target.checked})} />}
                    label={t('driver_table.arriving_path')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={showPaths.taken} onChange={(e) => setShowPaths({...showPaths, taken: e.target.checked})} />}
                    label={t('driver_table.taken_path')}
                  />
                  <Box sx={{ flex: 1 }} />
                  {/* Expand button */}
                  <button
                    onClick={() => setJourneyMapFullscreen(true)}
                    style={{
                      background: 'rgba(34,34,58,0.8)',
                      border: 'none',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                    title={t('common.expand_map')}
                  >
                    <FiMaximize2 size={20} />
                  </button>
                </Box>
                <Box sx={{ width: '100%', height: 320, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                  <MapContainer
                    center={driver.journeys[selectedJourneyIdx].supposed_path?.[0] || [0,0]}
                    zoom={13}
                    style={{ width: '100%', height: '100%' }}
                    scrollWheelZoom={true}
                    dragging={true}
                    doubleClickZoom={true}
                    zoomControl={true}
                    attributionControl={true}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
                    />
                    {/* Supposed Path (grey) */}
                    {showPaths.supposed && driver.journeys[selectedJourneyIdx].supposed_path && (
                      <Polyline positions={driver.journeys[selectedJourneyIdx].supposed_path} color="#888" weight={5} />
                    )}
                    {/* Driver Arriving Path (blue) */}
                    {showPaths.arriving && driver.journeys[selectedJourneyIdx].driver_arriving_path && (
                      <Polyline positions={driver.journeys[selectedJourneyIdx].driver_arriving_path} color="#2196f3" weight={5} />
                    )}
                    {/* Taken Path (green) */}
                    {showPaths.taken && driver.journeys[selectedJourneyIdx].taken_path && (
                      <Polyline positions={driver.journeys[selectedJourneyIdx].taken_path} color="#43a047" weight={5} />
                    )}
                  </MapContainer>
                </Box>
                {/* Fullscreen Map Dialog */}
                <Dialog
                  open={journeyMapFullscreen}
                  onClose={() => setJourneyMapFullscreen(false)}
                  maxWidth={false}
                  fullWidth
                  PaperProps={{
                    sx: {
                      background: 'rgba(25,25,50,0.95)',
                      borderRadius: 0,
                      m: 0,
                      p: 0,
                      width: '100vw',
                      height: '100vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                >
                  <button
                    onClick={() => setJourneyMapFullscreen(false)}
                    style={{
                      position: 'absolute',
                      top: 24,
                      right: 24,
                      zIndex: 2000,
                      background: 'rgba(34,34,58,0.8)',
                      border: 'none',
                      borderRadius: '50%',
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                    title={t('common.close_fullscreen_map')}
                  >
                    <FiX size={28} />
                  </button>
                  {/* Path toggles in fullscreen */}
                  <Box sx={{ position: 'absolute', top: 24, left: 24, zIndex: 2000, display: 'flex', gap: 2, alignItems: 'center', background: 'rgba(25,25,50,0.85)', borderRadius: 2, p: 1 }}>
                    <FormControlLabel
                      control={<Checkbox checked={showPaths.supposed} onChange={(e) => setShowPaths({...showPaths, supposed: e.target.checked})} />}
                      label={t('driver_table.supposed_path')}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={showPaths.arriving} onChange={(e) => setShowPaths({...showPaths, arriving: e.target.checked})} />}
                      label={t('driver_table.arriving_path')}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={showPaths.taken} onChange={(e) => setShowPaths({...showPaths, taken: e.target.checked})} />}
                      label={t('driver_table.taken_path')}
                    />
                  </Box>
                  <Box sx={{ width: '100vw', height: '100vh', borderRadius: 0, overflow: 'hidden', position: 'relative' }}>
                    <MapContainer
                      center={driver.journeys[selectedJourneyIdx].supposed_path?.[0] || [0,0]}
                      zoom={13}
                      style={{ width: '100%', height: '100%' }}
                      scrollWheelZoom={true}
                      dragging={true}
                      doubleClickZoom={true}
                      zoomControl={true}
                      attributionControl={true}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
                      />
                      {showPaths.supposed && driver.journeys[selectedJourneyIdx].supposed_path && (
                        <Polyline positions={driver.journeys[selectedJourneyIdx].supposed_path} color="#888" weight={5} />
                      )}
                      {showPaths.arriving && driver.journeys[selectedJourneyIdx].driver_arriving_path && (
                        <Polyline positions={driver.journeys[selectedJourneyIdx].driver_arriving_path} color="#2196f3" weight={5} />
                      )}
                      {showPaths.taken && driver.journeys[selectedJourneyIdx].taken_path && (
                        <Polyline positions={driver.journeys[selectedJourneyIdx].taken_path} color="#43a047" weight={5} />
                      )}
                    </MapContainer>
                  </Box>
                </Dialog>
              </>
            )}
          </Section>
        )}
      </DialogContent>

      <Dialog
        open={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(25,25,50,0.95)',
            borderRadius: 0,
            m: 0,
            p: 0,
            width: '100vw',
            height: '100vh',
          },
        }}
      >
        <button
          onClick={() => setMapFullscreen(false)}
          style={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 2000,
            background: 'rgba(34,34,58,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          title={t('common.close_fullscreen_map')}
        >
          <FiX size={28} />
        </button>
        <DriverLocationMap
          lat={driver.current_latitude}
          lng={driver.current_longitude}
          name={`${driver.first_name} ${driver.last_name}`}
          height="100vh"
          fullscreen
        />
      </Dialog>

      {/* Lightbox Modal for images */}
      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ ...lightbox, open: false })}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(0,0,0,0)',
            borderRadius: 0,
            m: 0,
            p: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          },
        }}
      >
        {/* X close icon */}
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 2000,
            background: 'rgba(34,34,58,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          onClick={() => setLightbox({ ...lightbox, open: false })}
          title={t('common.close_preview')}
        >
          <FiX size={28} />
        </Box>
        {/* Left arrow */}
        {lightbox.images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              left: 24,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2000,
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onClick={() => setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }))}
            title={t('common.previous')}
          >
            <FiChevronLeft size={28} />
          </Box>
        )}
        {/* Right arrow */}
        {lightbox.images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              right: 24,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2000,
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onClick={() => setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }))}
            title={t('common.next')}
          >
            <FiChevronRight size={28} />
          </Box>
        )}
        {/* Image preview */}
        <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {lightbox.images[lightbox.index] && (
            <img
              src={lightbox.images[lightbox.index]}
              alt={lightbox.group + ' ' + (lightbox.index + 1)}
              style={{
                maxWidth: 'calc(100vw - 64px)',
                maxHeight: 'calc(100vh - 64px)',
                borderRadius: 16,
                boxShadow: '0 4px 32px 0 rgba(0,0,0,0.25)',
                objectFit: 'contain',
                background: 'none',
                margin: 0,
                padding: 0,
                display: 'block',
              }}
            />
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default DriverDetailsModal; 